import { handleError } from "@/utils/error";
import {
  useChangeOfBrokerMutation,
  useInvalidateCobBreakdown,
  useUpdateCobStatus,
} from "creso-core/hooks";
import { COBClientDetail, COBFolio } from "creso-core/types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export const CobContext = createContext<{
  selectedClient: COBClientDetail | null;
  setSelectedClient: (
    clientOrUpdater:
      | COBClientDetail
      | null
      | ((prev: COBClientDetail | null) => COBClientDetail | null)
  ) => void;
  markCobFormInTransit: (
    client: COBClientDetail | null,
    folioIds: number[]
  ) => Promise<void>;
  initiateCobFormDownload: (
    client: COBClientDetail | null,
    foliosToDownload: COBFolio[]
  ) => Promise<void>;
} | null>(null);

export function CobProvider({ children }: { children: React.ReactNode }) {
  const [selectedClient, setSelectedClient] = useState<COBClientDetail | null>(
    null
  );

  const { mutateAsync: updateCobStatus } = useUpdateCobStatus();

  const invalidateCobBreakdown = useInvalidateCobBreakdown();

  const markCobFormInTransit = useCallback(
    async (client: COBClientDetail | null, folioIds: number[]) => {
      if (!client) return;
      try {
        await updateCobStatus({
          status: "IN_TRANSIT",
          folioIds,
          investmentAccountId: client?.investment_account_id ?? "",
        });
        invalidateCobBreakdown();
        setSelectedClient((prev) => {
          if (!prev) return prev;
          const newClient = { ...prev };
          newClient.folio_details = newClient.folio_details.map((folio) => {
            if (folioIds.includes(folio.folio)) {
              return { ...folio, status: "IN_TRANSIT" };
            }
            return folio;
          });
          return newClient;
        });
      } catch (error) {
        handleError(error, "Failed to update folio status");
      }
    },
    [updateCobStatus, invalidateCobBreakdown]
  );

  const { mutateAsync: createCobRequest } = useChangeOfBrokerMutation();

  const initiateCobFormDownload = useCallback(
    async (client: COBClientDetail | null, foliosToDownload: COBFolio[]) => {
      if (!client) return;
      const preference = foliosToDownload.reduce(
        (acc, fd) => {
          acc[fd.broker_code] = acc[fd.broker_code] || [];
          acc[fd.broker_code].push(fd.folio);
          return acc;
        },
        {} as Record<string, number[]>
      );
      await createCobRequest({
        investmentAccountId: client?.investment_account_id ?? "",
        preference,
      });
    },
    [createCobRequest]
  );

  const contextValue = useMemo(
    () => ({
      selectedClient,
      setSelectedClient,
      markCobFormInTransit,
      initiateCobFormDownload,
    }),
    [
      selectedClient,
      setSelectedClient,
      markCobFormInTransit,
      initiateCobFormDownload,
    ]
  );

  return (
    <CobContext.Provider value={contextValue}>{children}</CobContext.Provider>
  );
}

export function useCob() {
  const context = useContext(CobContext);
  if (!context) {
    throw new Error("useCob must be used within a CobProvider");
  }
  return context;
}
